import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import isObject from 'lodash/isObject';
import { Card, Col, Row } from 'antd';
import { EMPTY_VALUE_PLACEHOLDER } from '../../../../../const/system';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import urlPageTemplates from '../../../../../urls/urlPageTemplates';
import urlPageTemplatesVisualizations from '../../../../../urls/urlPageTemplatesVisualizations';
import urlPageTemplatesVisualizationsDetails from '../../../../../urls/urlPageTemplatesVisualizationsDetails';
import handleResponse from '../../../../../lib/handleResponse';
import transformErrors from '../../../../../lib/transformErrors';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import CollapsibleText from '../../../../../components/CollapsibleText';
import Spinner from '../../../../../components/Spinner';
import FormCreateEditTemplatesVisualization from '../../../forms/FormCreateEditTemplatesVisualization';
import {
  useCreateTemplatesVisualizationMutation,
  useUpdateTemplatesVisualizationMutation,
  useGetTemplatesVisualizationQuery,
} from '../../../api/templatesVisualizationsApiSlice';



const PageCreateEditTemplatesVisualization = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { visualizationId } = useParams();
  const [ errors, setFormErrors ] = useState({});

  const isEditTemplateVisualization = pathname.endsWith('edit');

  const { data = { data: {} }, isFetching } = useGetTemplatesVisualizationQuery(visualizationId, { skip: !isEditTemplateVisualization });
  const [ createTemplatesVisualization, { isLoading: isCreatingVisualization } ] = useCreateTemplatesVisualizationMutation();
  const [ updateTemplatesVisualization, { isLoading: isUpdatingVisualization } ] = useUpdateTemplatesVisualizationMutation();

  const handleSubmit = async (values) => {
    let action;

    if (isEditTemplateVisualization) {
      action = updateTemplatesVisualization({ id: visualizationId, values });
    } else {
      action = createTemplatesVisualization({ values });
    }

    action.unwrap()
      .then(() => handleResponse(
        'success',
        intl,
        isEditTemplateVisualization
          ? 'ui-general-updated-successfully'
          : 'ui-general-created-successfully',
        [ () => navigate(urlPageTemplatesVisualizations()) ],
      ))
      .catch((error) => handleResponse(
        'error',
        intl,
        isEditTemplateVisualization ? 'ui-general-update-failed' : 'ui-general-create-failed',
        [ () => {
          if (isObject(error?.data?.errors)) {
            setFormErrors(transformErrors(error?.data?.errors));
          }
        } ],
        error,
      ));
  };

  const breadCrumbActive = !isEditTemplateVisualization
    ? <IntlMessages id="ui-general-create" />
    : <IntlMessages id="ui-general-edit" />;

  const breadCrumbParent2 = !isEditTemplateVisualization ? null
    : <Link to={urlPageTemplatesVisualizations()}><IntlMessages id='templates-visualizations-breadcrumbs' /></Link>;

  const breadCrumbParent3 = !isEditTemplateVisualization ?
    <Link to={urlPageTemplatesVisualizations()}><IntlMessages id='templates-visualizations-breadcrumbs' /></Link> : (
      <Link to={urlPageTemplatesVisualizationsDetails({ visualizationId })}>
        <CollapsibleText text={data?.data?.title ?? EMPTY_VALUE_PLACEHOLDER} />
      </Link>
    );

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageTemplates()}>
              <IntlMessages id='templates-breadcrumbs' />
            </Link>
          }
          breadCrumbParent2={breadCrumbParent2}
          breadCrumbParent3={breadCrumbParent3}
          breadCrumbActive={breadCrumbActive}
        />
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <Spinner spinning={isFetching}>
              <FormCreateEditTemplatesVisualization
                initialValues={!isEditTemplateVisualization ? {
                  title: '',
                  html: '',
                  template_id: null,
                  description: null,
                  is_default: false,
                } : data?.data}
                initialErrors={errors}
                isSubmitting={isCreatingVisualization || isUpdatingVisualization}
                onSubmit={handleSubmit}
                isEdit={isEditTemplateVisualization}
              />
            </Spinner>
          </Card>
        </Col>
      </Row>
    </>
  );
};


export default PageCreateEditTemplatesVisualization;

